/**
 * Companies — screen 10, ported from the validated mockup.
 *
 * The company decides which rows a person sees, which reports they may open and what
 * colour they are drawn in, so it is maintained here rather than as a dropdown repeated
 * across three screens.
 *
 * ⚠ **One field the mockup does not have, and has to: the Monday labels.** That list IS
 * the row scope — `contractorScope` returns it and `matchesScope` filters on it — and an
 * empty one means "no rows match", which on screen is indistinguishable from a report that
 * is simply empty. Without this field, adding a contractor produces a company whose people
 * sign in successfully, open the report, see nothing, and report it broken.
 *
 * ⚠ A label is matched exactly as the board spells it, so this screen never lower-cases or
 * re-spaces one: `CIF - Circet` typed as `cif-circet` would match nothing, silently.
 *
 * ⚠ Nothing destructive happens without its blast radius on screen first. "Are you sure?"
 * is not a useful question; "this stops 4 people signing in and revokes 2 report grants" is.
 */

import { useCallback, useEffect, useState } from "react";

import {
  COMPANY_PALETTE,
  companyImpact,
  formatDayMonthYear,
  formatNumber,
  isValidCompanyCode,
  normaliseMondayLabels,
  type AdminCompanyRow,
  type CompanyKind,
} from "@quality-field/shared";
import { AlertTriangle, Building2, Pencil, PlusCircle, Power, X } from "lucide-react";
import { toast } from "sonner";

import { Dialog, PickCard } from "@/components/Dialog";
import { ApiError, apiFetch } from "@/lib/api-client";

interface Directory {
  companies: AdminCompanyRow[];
}

export function CompanyManagement() {
  const [directory, setDirectory] = useState<Directory | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState<AdminCompanyRow | "new" | null>(null);
  const [confirming, setConfirming] = useState<AdminCompanyRow | null>(null);

  const load = useCallback(async () => {
    try {
      setDirectory(await apiFetch<Directory>("/admin/companies"));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Returns whether it worked, so a dialog can stay open on a refusal with its form intact. */
  const run = useCallback(
    async (work: () => Promise<unknown>, done: string): Promise<boolean> => {
      setBusy(true);
      try {
        await work();
        await load();
        toast.success(done);
        return true;
      } catch (err) {
        // The server's own sentence — "Circet already claims CIF - Circet", "that colour is
        // taken" — is the thing the administrator has to act on.
        toast.error(err instanceof ApiError ? err.message : "The change could not be saved.");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  if (loading) return <div className="px-4 py-6 text-sm text-slate-400 lg:px-8">Loading…</div>;

  if (failed || !directory) {
    return (
      <div className="px-4 py-6 lg:px-8">
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <h3 className="font-semibold text-slate-700">The company list could not be loaded</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Reload the page, or try again shortly.</p>
        </div>
      </div>
    );
  }

  const { companies } = directory;
  const contractors = companies.filter((c) => c.kind === "CONTRACTOR");
  const internal = companies.filter((c) => c.kind === "INTERNAL");
  const people = companies.reduce((sum, c) => sum + c.users, 0);
  const grants = companies.reduce((sum, c) => sum + c.reportGrants, 0);

  return (
    <div className="max-w-[1400px] space-y-5 px-4 py-6 lg:px-8">
      <div className="flex flex-wrap items-center gap-4">
        <div className="gradient-band flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-slate-900">Companies</h2>
          <p className="text-sm text-slate-500">
            Who may sign in, which rows they see, and the colour they are drawn in.
          </p>
        </div>
        <button type="button" className="btn gradient-btn" onClick={() => setEditing("new")}>
          <PlusCircle className="h-4 w-4" /> Add Company
        </button>
      </div>

      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi accent="purple" label="Contractors" value={contractors.length} note="scoped to their own rows" />
        <Kpi accent="orange" label="Internal" value={internal.length} note="unscoped, see everything" />
        <Kpi accent="rose" label="People" value={people} note="across all companies" />
        <Kpi accent="emerald" label="Report grants" value={grants} note="held by companies" />
      </section>

      <section className="card overflow-visible">
        <div className="gradient-band flex items-center justify-between rounded-t-xl px-5 py-3 text-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Companies</span>
          <span className="text-xs text-white/85">
            {companies.length} companies · {people} people
          </span>
        </div>
        <div className="custom-scrollbar overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 44 }} />
                <th>Company</th>
                <th>Type &amp; Monday labels</th>
                <th>Contact</th>
                <th className="text-right">People</th>
                <th className="text-right">Grants</th>
                <th>Status</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => (
                <tr key={company.id} className={company.isActive ? "" : "opacity-60"}>
                  <td>
                    <span className="swatch block" style={{ background: company.colour }} />
                  </td>
                  <td>
                    <div className="font-semibold text-slate-800">{company.name}</div>
                    <div className="mono text-xs text-slate-400">
                      {company.code} · since {formatDayMonthYear(new Date(company.since))}
                    </div>
                    {company.signInDomains.length > 0 && (
                      <div className="mono text-xs text-slate-400">{company.signInDomains.join(", ")}</div>
                    )}
                  </td>
                  <td>
                    <span className={`pill pill-${company.kind === "INTERNAL" ? "orange" : "purple"}`}>
                      {company.kind === "INTERNAL" ? "Internal" : "Contractor"}
                    </span>
                    {/* ⚠ Directly under the type, because this list IS the row scope. Shown
                        comma-separated, exactly as stored: a label holds spaces of its own
                        (`CIF - Circet`), so a gap alone could not tell two labels apart. */}
                    <div className="mono mt-1 text-xs text-slate-600">
                      {company.mondayCompanyLabels.length === 0 ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        company.mondayCompanyLabels.join(", ")
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="text-slate-700">{company.contactName ?? <span className="text-slate-300">—</span>}</div>
                    {company.contactEmail && <div className="text-xs text-slate-400">{company.contactEmail}</div>}
                  </td>
                  <td className="num text-right text-slate-700">
                    {company.users}
                    {company.users !== company.activeUsers && (
                      <span className="text-xs text-slate-400"> ({company.activeUsers} active)</span>
                    )}
                  </td>
                  <td className="num text-right text-slate-700">{company.reportGrants}</td>
                  <td>
                    {company.isActive ? (
                      <span className="pill pill-emerald">
                        <span className="pill-dot" style={{ background: "currentColor" }} />
                        Active
                      </span>
                    ) : (
                      <span className="pill pill-slate">Inactive</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    <button
                      type="button"
                      className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                      title="Edit"
                      disabled={busy}
                      onClick={() => setEditing(company)}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {company.isActive ? (
                      <button
                        type="button"
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                        title={
                          company.kind === "INTERNAL"
                            ? "Unifiber's own company cannot be deactivated"
                            : "Deactivate this company"
                        }
                        disabled={busy || company.kind === "INTERNAL"}
                        onClick={() => setConfirming(company)}
                      >
                        <Power className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () => apiFetch(`/admin/companies/${company.id}/reactivate`, { method: "POST" }),
                            `${company.name} can sign in again.`,
                          )
                        }
                      >
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {confirming && (
        <ImpactPanel
          company={confirming}
          busy={busy}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            const name = confirming.name;
            const removable = companyImpact(confirming).removable;
            setConfirming(null);
            void run(
              () => apiFetch(`/admin/companies/${confirming.id}/deactivate`, { method: "POST" }),
              removable ? `${name} removed.` : `${name} deactivated — everyone in it is signed out.`,
            );
          }}
        />
      )}

      <p className="text-xs text-slate-400">
        A company is never deleted while it still has people — deactivating it stops sign-ins and takes it out of the
        pickers, while its history stays where it is and no report total moves.
      </p>

      {editing && (
        <CompanyDialog
          company={editing === "new" ? null : editing}
          takenColours={companies.filter((c) => editing === "new" || c.id !== editing.id).map((c) => c.colour)}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={(body) => {
            const target = editing;
            const isNew = target === "new";
            void run(
              () =>
                apiFetch(isNew ? "/admin/companies" : `/admin/companies/${target.id}`, {
                  method: isNew ? "POST" : "PATCH",
                  body: JSON.stringify(body),
                }),
              isNew ? `${String(body.name)} created. It has no report access and no people yet.` : "Saved.",
            ).then((ok) => {
              if (ok) setEditing(null);
            });
          }}
        />
      )}
    </div>
  );
}

function Kpi({
  accent,
  label,
  value,
  note,
  noteTone,
}: {
  accent: "purple" | "orange" | "rose" | "emerald";
  label: string;
  value: number;
  note: string;
  noteTone?: string;
}) {
  return (
    <div className={`glass-card card-accent-${accent} rounded-xl p-5`}>
      <p className="eyebrow">{label}</p>
      <p className="num mt-1 text-3xl font-bold text-slate-900">{formatNumber(value)}</p>
      <p className="mt-1 text-xs" style={noteTone ? { color: noteTone } : { color: "#64748b" }}>
        {note}
      </p>
    </div>
  );
}

/**
 * The blast radius, stated before the button exists.
 *
 * ⚠ Every figure is real and comes from the server. The panel deliberately promises two
 * things it can keep: the people who lose access and the grants that go. It does NOT say
 * "nothing else changes" unless nothing else does.
 */
function ImpactPanel({
  company,
  busy,
  onCancel,
  onConfirm,
}: {
  company: AdminCompanyRow;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const impact = companyImpact(company);

  return (
    <section className="card card-accent-red overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
        <AlertTriangle className="h-4 w-4" style={{ color: "#b91c1c" }} />
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
          {impact.removable ? `Remove ${company.name}?` : `Deactivate ${company.name}?`}
        </span>
        <button type="button" className="ml-auto text-slate-400 hover:text-slate-700" onClick={onCancel} aria-label="Cancel">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-4 p-5">
        <p className="text-sm text-slate-600">
          {impact.removable
            ? `${company.name} holds no people, so it can be removed outright. Nothing else changes.`
            : `${company.name} cannot be removed — it still holds people. Deactivating stops sign-ins and takes it out of the pickers; its people and their history stay exactly where they are, and no report total moves.`}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Stat label="People signed out" value={impact.users} tone={impact.users ? "#b91c1c" : undefined} />
          <Stat
            label="Report grants revoked"
            value={impact.reportGrants}
            tone={impact.reportGrants ? "#b91c1c" : undefined}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button type="button" className="btn btn-outline" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            style={{ background: "#dc2626", color: "#fff" }}
            disabled={busy}
            onClick={onConfirm}
          >
            {impact.removable ? `Remove ${company.name}` : `Deactivate ${company.name}`}
          </button>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg p-3" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
      <p className="eyebrow">{label}</p>
      <p className="num mt-0.5 text-xl font-bold" style={{ color: tone ?? "#0f172a" }}>
        {formatNumber(value)}
      </p>
    </div>
  );
}

function CompanyDialog({
  company,
  takenColours,
  busy,
  onClose,
  onSave,
}: {
  company: AdminCompanyRow | null;
  takenColours: string[];
  busy: boolean;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState(company?.name ?? "");
  const [code, setCode] = useState(company?.code ?? "");
  const [kind, setKind] = useState<CompanyKind>(company?.kind ?? "CONTRACTOR");
  const [colour, setColour] = useState(
    company?.colour ?? COMPANY_PALETTE.find((c) => !takenColours.includes(c)) ?? COMPANY_PALETTE[0],
  );
  const [labelsText, setLabelsText] = useState(company?.mondayCompanyLabels.join(", ") ?? "");
  // ⚠ The empty-labels warning is real but must not be RED before anybody has typed
  // anything — a dialog that opens complaining reads as a broken screen. Neutral until
  // they have touched the field, red once they have emptied it themselves.
  const [labelsTouched, setLabelsTouched] = useState(false);
  const [domains, setDomains] = useState(company?.signInDomains.join(", ") ?? "");
  const [contactName, setContactName] = useState(company?.contactName ?? "");
  const [contactEmail, setContactEmail] = useState(company?.contactEmail ?? "");

  const split = (value: string) =>
    value
      .split(/[,\s]+/)
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);

  // ⚠ Labels split on commas and line breaks ONLY, and keep their case: a label holds spaces
  // of its own (`CIF - Circet`), and the scope matches the board's spelling exactly.
  const labels = normaliseMondayLabels(labelsText.split(/[,\n]/));
  const codeProblem = code && !isValidCompanyCode(code) ? "Lower-case letters, digits and hyphens only." : null;
  const missingLabels = kind === "CONTRACTOR" && labels.length === 0;

  return (
    <Dialog title={company ? `Edit ${company.name}` : "Add Company"} onClose={onClose} size="wide">
      <form
        className="space-y-5 p-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (codeProblem || missingLabels) return;
          onSave({
            code,
            name,
            kind,
            colour,
            // An internal company is never scoped, so whatever was typed before switching
            // the type is not sent — the server would refuse it.
            mondayCompanyLabels: kind === "CONTRACTOR" ? labels : [],
            signInDomains: split(domains),
            contactName: contactName.trim() || null,
            contactEmail: contactEmail.trim() || null,
          });
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="eyebrow">Company name</label>
            <input
              type="text"
              className="field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nexus Telecom"
              required
            />
          </div>
          <div className="space-y-1.5">
            <label className="eyebrow">Code</label>
            <input
              type="text"
              className="field mono"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              placeholder="nexus"
              required
            />
            <p className="text-xs" style={codeProblem ? { color: "#b91c1c" } : { color: "#94a3b8" }}>
              {codeProblem ?? "Lower-case, no spaces. Used in exports and links."}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="eyebrow">Type</label>
          <div className="grid gap-3 sm:grid-cols-2">
            <PickCard
              selected={kind === "CONTRACTOR"}
              onSelect={() => setKind("CONTRACTOR")}
              title="Contractor"
              description="Sees only its own rows."
              disabled={company !== null && company.kind !== "CONTRACTOR"}
              disabledReason="Changing the type would change what everybody in it can see"
            />
            <PickCard
              selected={kind === "INTERNAL"}
              onSelect={() => setKind("INTERNAL")}
              title="Internal"
              description="Unifiber staff. Unscoped, sees every row."
              disabled={company !== null && company.kind !== "INTERNAL"}
              disabledReason="Changing the type would change what everybody in it can see"
            />
          </div>
        </div>

        {/*
          ⚠ Not in the mockup, and the screen does not work without it. This list IS the row
          scope — `contractorScope` returns it, `matchesScope` filters on it — so a
          contractor with no label matches no rows, and a contractor with a mistyped one
          matches no rows just as quietly.
        */}
        {kind === "CONTRACTOR" && (
          <div className="space-y-1.5">
            <label className="eyebrow">Monday labels</label>
            <input
              type="text"
              className="field mono"
              value={labelsText}
              onChange={(e) => {
                setLabelsTouched(true);
                setLabelsText(e.target.value);
              }}
              placeholder="CIF - Circet"
            />
            {missingLabels ? (
              <p className="text-xs" style={{ color: labelsTouched ? "#b91c1c" : "#94a3b8" }}>
                Enter at least one. Without a label this company matches no rows at all — its people would open the
                report and find it empty.
              </p>
            ) : (
              <p className="text-xs text-slate-400">
                The <span className="mono">Build contractor</span> values on the Monday board that belong to this
                company, written exactly as the board writes them — capitals and spaces count. Separate several with
                commas.
              </p>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="eyebrow">Sign-in domains</label>
          <input
            type="text"
            className="field mono"
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            placeholder="nexus.be, nexus-telecom.fr"
          />
          <p className="text-xs text-slate-400">
            {/* ⚠ Informational only, and the schema says so too. Contractor staff often
                sign in with personal addresses, so membership is always the explicit
                company on the person and never inferred from their domain. */}
            A note for whoever invites people — nothing is enforced on it, because half of these staff sign in with a
            personal address.
          </p>
        </div>

        <div className="space-y-2">
          <label className="eyebrow">Chart colour</label>
          <div className="flex flex-wrap gap-2">
            {COMPANY_PALETTE.map((option) => {
              const taken = takenColours.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  className={`colour-pick${colour === option ? " sel" : ""}`}
                  style={{ background: option }}
                  disabled={taken}
                  title={taken ? "Already used by another company" : option}
                  aria-label={option}
                  onClick={() => setColour(option)}
                />
              );
            })}
          </div>
          <p className="text-xs text-slate-400">
            Used on every chart in every report. Two companies cannot share one — it is the only thing telling them
            apart on a stacked bar.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="eyebrow">Main contact</label>
            <input
              type="text"
              className="field"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Jan Peeters"
            />
          </div>
          <div className="space-y-1.5">
            <label className="eyebrow">Contact email</label>
            <input
              type="email"
              className="field"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="j.peeters@nexus.be"
            />
          </div>
        </div>

        {!company && (
          <div className="flex items-start gap-2.5 rounded-lg px-3.5 py-2.5" style={{ background: "#eff6ff", border: "1px solid #bedbfe" }}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#1d4fd7" }} />
            <p className="text-xs" style={{ color: "#1d4fd7" }}>
              A new company starts with <span className="font-semibold">no report access and no people</span>. Grant it
              a report on <span className="font-semibold">Report Access</span>, then invite its staff on{" "}
              <span className="font-semibold">Users</span>.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button type="submit" className="btn gradient-btn" disabled={busy || codeProblem !== null || missingLabels}>
            {company ? "Save Changes" : "Create Company"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
